import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  ArrowRight, ChevronDown, Compass, Hammer, Award, Wallet,
  MapPin, CheckCircle2, ChevronLeft, ChevronRight,
} from "lucide-react";
import { FadeUp, SectionLabel } from "../components/Motion";
import DynamicMultiForm from "../components/DynamicMultiForm";
import { useEffect, useState } from "react";
import {
  services,
  FEATURE_IMAGE,
} from "../data/projects";

import { getProjects } from "../services/projectsApi";
import { getTestimonials } from "../services/testimonialsApi";
import { mapApiProject, makeAbsoluteUrl } from "../utils/projectMapper";
import { buildWhatsappUrl, PHONE_TEL } from "../lib/whatsapp";



const whyItems = [
  {
    title: "Smart Planning",
    desc: "Well-planned spaces with functional design and detail.",
    icon: Compass,
  },
  {
    title: "Quality Building",
    desc: "Built with trusted materials and strong standards.",
    icon: Hammer,
  },
  {
    title: "Proven Experience",
    desc: "Decades of expertise delivering reliable results.",
    icon: Award,
  },
  {
    title: "Cost Efficiency",
    desc: "Value-driven solutions without quality compromise.",
    icon: Wallet,
  },
];

const aboutHighlights = [
  "Quality Construction",
  "Trusted Expertise",
  "Cost Effective",
];

// Construction/building background video sources (Mixkit — free, no attribution).
const HERO_VIDEO_SOURCES = [
  "https://assets.mixkit.co/videos/4010/4010-1080.mp4",
  "https://assets.mixkit.co/videos/4010/4010-720.mp4",
];

function TestimonialAvatar({ photoUrl, clientName, clientInitial }) {
  const [photoFailed, setPhotoFailed] = useState(false);

  if (photoUrl && !photoFailed) {
    return (
      <img
        src={photoUrl}
        alt={clientName}
        className="h-11 w-11 rounded-full object-cover shadow-md"
        loading="lazy"
        onError={() => setPhotoFailed(true)}
      />
    );
  }

  return (
    <div
      className="h-11 w-11 rounded-full bg-gradient-to-r from-[#7A2E5D] to-pink-400 flex items-center justify-center text-white font-semibold shadow-md"
      aria-hidden="true"
    >
      {clientInitial}
    </div>
  );
}

export default function Home({ onEnquire }) {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [testimonialsError, setTestimonialsError] = useState("");

  useEffect(() => {
    async function loadProjects() {
      try {
        setProjectsLoading(true);
        setProjectsError("");

        const apiProjects = await getProjects();
        setProjects(apiProjects.map(mapApiProject));
      } catch (error) {
        console.error("Home projects loading error:", error);
        setProjectsError("Unable to load projects.");
      } finally {
        setProjectsLoading(false);
      }
    }

    loadProjects();
  }, []);

  useEffect(() => {
    async function loadTestimonials() {
      try {
        setTestimonialsLoading(true);
        setTestimonialsError("");

        const apiTestimonials = await getTestimonials();
        setTestimonials(apiTestimonials);
      } catch (error) {
        console.error("Home testimonials loading error:", error);
        setTestimonialsError("Unable to load testimonials.");
      } finally {
        setTestimonialsLoading(false);
      }
    }

    loadTestimonials();
  }, []);

  const featured = projects;
  const homeGalleryImages = projects
    .flatMap((project) =>
      Array.isArray(project.galleryImages) ? project.galleryImages : []
    )
    .slice(0, 9);
  const waUrl = buildWhatsappUrl(
    "Hello, I'd like to know more about Sanggavai Constructions."
  );

  const handleProjectEnquire = (projectName) => {
    if (typeof onEnquire === "function") {
      onEnquire({ projectName });
    }
  };

  return (
    <div data-testid="home-page" className="bg-[#FAFAFA]">
      {/* HERO with background video */}
      <section data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden">
        <div className="absolute inset-0 bg-[#101012]">
          {/* The local poster matches the footage, so refresh starts seamlessly. */}
          <motion.video
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 12, ease: "easeOut" }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="construction-video-poster.png"
            data-testid="hero-video"
            className="block w-full h-full object-cover"
          >
            {HERO_VIDEO_SOURCES.map((src) => (
              <source key={src} src={src} type="video/mp4" />
            ))}
          </motion.video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        </div>

      <div className="relative z-10 min-h-[100svh] flex flex-col items-center justify-center text-center px-6 pt-20">
  
  <motion.p
    initial={{ opacity: 0, y: 20 }} 
    animate={{ opacity: 1, y: 0 }} 
    transition={{ duration: 0.9 }}
    className="text-xs md:text-sm tracking-[0.32em] text-white/80 uppercase mb-6"
  >
    Building Excellence
  </motion.p>

  <motion.h1
    initial={{ opacity: 0, y: 30 }} 
    animate={{ opacity: 1, y: 0 }} 
    transition={{ duration: 1.1, delay: 0.15 }}
    className="font-serif text-white text-[44px] leading-[1.04] sm:text-6xl md:text-7xl lg:text-8xl md:leading-[1.02] tracking-tight max-w-5xl"
  >
    Over <span className="italic font-light">34 Years</span> of <br />
    Sanggavai <span className="italic font-light">Constructions</span>
  </motion.h1>

  <motion.p
    initial={{ opacity: 0, y: 20 }} 
    animate={{ opacity: 1, y: 0 }} 
    transition={{ duration: 0.9, delay: 0.4 }}
    className="mt-8 text-white/90 text-lg md:text-xl max-w-2xl"
  >
    A trusted name delivering high-quality, durable construction with a 
    personalized approach — creating spaces that stand the test of time.
  </motion.p>

  <motion.div
    initial={{ opacity: 0, y: 20 }} 
    animate={{ opacity: 1, y: 0 }} 
    transition={{ duration: 0.9, delay: 0.6 }}
    className="mt-10 flex flex-col sm:flex-row items-center gap-4"
  >
    <Link
      to="/projects"
      data-testid="hero-projects-btn"
      className="group inline-flex items-center gap-2 brand-gradient text-white rounded-full px-8 py-4 font-medium text-base hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300"
    >
      View Projects 
      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
    </Link>

    <Link
      to="/contact"
      data-testid="hero-contact-btn"
      className="inline-flex items-center gap-2 border border-white/70 text-white rounded-full px-8 py-4 font-medium text-base hover:bg-white hover:text-[#1A1A1A] transition-all duration-300"
    >
      Contact
    </Link>
  </motion.div>

</div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <ChevronDown className="h-6 w-6 text-white/80 animate-bounce" />
        </div>
      </section>

      {/* ABOUT PREVIEW — split layout, glass card with gradient highlight */}
   <section data-testid="about-preview" className="py-24 md:py-32">
  <div className="container mx-auto px-6 md:px-12 max-w-6xl">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">

      {/* Image */}
      <FadeUp className="md:col-span-6 order-1">
        <div
          className="relative overflow-hidden aspect-[4/5] md:aspect-[4/4.5] group shadow-xl"
          style={{ borderRadius: "20px" }}
        >
          <img
            src={FEATURE_IMAGE}
            alt="Sanggavai construction"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />

          
        </div>
      </FadeUp>

      {/* Content */}
      <FadeUp delay={0.1} className="md:col-span-6 order-2">
        <SectionLabel>About Us</SectionLabel>

        {/* Title */}
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] leading-[1.08] tracking-tight mt-5">
          Building Trust for{" "}
          <span className="brand-gradient-text italic">34+ Years</span> of Excellence
        </h2>

        {/* Paragraph */}
        <p className="mt-8 text-[#555] text-lg md:text-xl leading-relaxed max-w-xl">
          Sanggavai Constructions is built on passion, expertise, and strong civil engineering
          knowledge, delivering high-quality construction across a wide range of projects.
          Over the years, we have successfully completed numerous developments in and around
          Chennai, earning the trust of our clients through consistent quality, reliability,
          and superior workmanship.
        </p>

        <p className="mt-4 text-[#555] text-lg md:text-xl leading-relaxed max-w-xl">
          Our mission is to create unique, long-lasting, and value-driven construction solutions
          that stand the test of time while consistently exceeding client expectations.
        </p>

        {/* Highlight card */}
        <div
          className="mt-10 bg-white/70 backdrop-blur-xl border border-black/5 shadow-md p-6 md:p-8"
          style={{ borderRadius: "20px" }}
        >
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {[
              "Superior Quality",
              "Trusted Service"
             
            ].map((h) => (
              <li key={h} className="flex items-center gap-3 text-[#1A1A1A] text-base md:text-lg">
                <CheckCircle2 className="h-5 w-5 text-[#7A2E5D] shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <Link
          to="/about"
          data-testid="about-know-more-btn"
          className="group mt-10 inline-flex items-center gap-2 text-[#7A2E5D] font-medium text-lg"
        >
          Know more{" "}
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </FadeUp>

    </div>
  </div>
</section>



  {/* FEATURED PROJECTS — Swiper carousel */}
        <section data-testid="featured-projects" className="py-24 md:py-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <SectionLabel>Featured Projects</SectionLabel>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] mt-5 tracking-tight">Signature addresses.</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="carousel-prev"
                  className="projects-prev h-11 w-11 rounded-full border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-all inline-flex items-center justify-center"
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  data-testid="carousel-next"
                  className="projects-next h-11 w-11 rounded-full border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-all inline-flex items-center justify-center"
                  aria-label="Next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <Link to="/projects" data-testid="featured-view-all" className="group inline-flex items-center gap-2 text-[#7A2E5D] font-medium">
                View all <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </FadeUp>

          <div className="mt-14 projects-carousel overflow-hidden -mx-6 md:mx-0 px-6 md:px-0">
            {projectsLoading ? (
              <p className="text-center text-[#555] py-16">
                Loading projects...
              </p>
            ) : projectsError ? (
              <p className="text-center text-red-600 py-16">
                {projectsError}
              </p>
            ) : featured.length === 0 ? (
              <p className="text-center text-[#555] py-16">
                No projects are available yet.
              </p>
            ) : (
              <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1.1}
              breakpoints={{
                640: { slidesPerView: 2, spaceBetween: 24 },
                1024: { slidesPerView: 2, spaceBetween: 28 },
                1280: { slidesPerView: 3, spaceBetween: 32 },
              }}
              navigation={{ prevEl: ".projects-prev", nextEl: ".projects-next" }}
              pagination={{ clickable: true, el: ".projects-pagination" }}
              autoplay={{ delay: 4500, disableOnInteraction: true }}
              loop
            >
              {featured.map((p) => (
                <SwiperSlide key={p.id} className="!h-auto">
                  <div
                    className="group bg-white border border-black/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col overflow-hidden"
                    style={{ borderRadius: "20px" }}
                  >
                    {/* Image */}
                    <Link to={`/projects/${p.slug}`} data-testid={`home-project-${p.slug}`} className="relative block overflow-hidden">
                      <img
                        src={p.cover}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                      />
                      {/* Category badge — top left */}
                      <span className="absolute top-4 left-4 bg-white text-[#7A2E5D] text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full shadow-sm">
                        {p.category}
                      </span>
                      {/* Status badge — top right */}
                      {p.statusKey === "completed" ? (
                        <span className="absolute top-4 right-4 bg-[#1A1A1A] text-white text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full shadow-sm">
                          {p.status || "Completed"}
                        </span>
                      ) : (
                        <span className="absolute top-4 right-4 brand-gradient text-white text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full shadow-sm">
                          {p.status || "Ongoing"}
                        </span>
                      )}
                    </Link>

                    {/* Content below image */}
                    <div className="px-6 py-6 flex flex-col flex-1">
                      <h3 className="font-serif text-2xl md:text-[26px] font-semibold leading-tight tracking-tight text-[#1A1A1A]">
                        {p.name}
                      </h3>
                      <p className="mt-2 text-[#555] text-sm md:text-base">
{p.category} <span className="text-[#7A2E5D]/40 mx-1">|</span> {p.config}
                        {p.size ? ` (${p.size})` : ""}
                      </p>
                      <p className="mt-2 text-[#555] text-sm md:text-base flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-[#7A2E5D] shrink-0" />
                        {p.locationShort}
                      </p>

                      {/* Buttons */}
                      <div className="mt-auto pt-6">
                        <div className="flex items-center gap-3 pt-4 border-t border-black/5">
                          {p.statusKey !== "completed" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleProjectEnquire(p.name);
                              }}
                              data-testid={`slide-enquire-${p.slug}`}
                              className="flex-1 brand-gradient text-white rounded-full px-5 py-2.5 font-medium text-sm"
                            >
                              Enquire
                            </button>
                          )}
                          <Link
                            to={`/projects/${p.slug}`}
                            data-testid={`slide-view-${p.slug}`}
                            className="flex-1 text-center border-2 border-[#7A2E5D] text-[#7A2E5D] rounded-full px-5 py-2.5 font-medium text-sm hover:bg-[#7A2E5D] hover:text-white transition-all"
                          >
                            View More
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
              </Swiper>
            )}

            {/* Pagination dots */}
            <div className="projects-pagination !relative !mt-10 flex justify-center gap-2" />
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
    <section data-testid="why-section" className="py-24 md:py-32 bg-[#F5F2F0]">
  <div className="container mx-auto px-6 md:px-12 max-w-7xl">

    <FadeUp className="text-center max-w-2xl mx-auto">
      <SectionLabel>Why Choose Us</SectionLabel>

      <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-5 tracking-tight">
        Built on quality. Driven by trust.
      </h2>

      <p className="mt-6 text-[#555] text-lg leading-relaxed">
        At Sanggavai Constructions, we deliver excellence through smart planning,
        quality execution, and long-term value in every project.
      </p>
    </FadeUp>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-16">
      {whyItems.map((item, i) => (
        <FadeUp key={item.title} delay={i * 0.08}>
          <div
            data-testid={`why-item-${i}`}
            className="bg-white p-10 h-full border border-black/5 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            style={{ borderRadius: "20px" }}
          >
            <div className="h-12 w-12 rounded-full brand-gradient flex items-center justify-center text-white">
              <item.icon className="h-5 w-5" />
            </div>

            <p className="mt-8 font-serif text-2xl text-[#1A1A1A] leading-snug">
              {item.title}
            </p>

            {/* Description Added */}
            <p className="mt-4 text-[#555] text-base leading-relaxed">
              {item.desc}
            </p>

          </div>
        </FadeUp>
      ))}
    </div>

  </div>
</section>

    

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-[#1A1A1A] text-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp>
            <SectionLabel className="!text-[#E85D8E]">Services</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 tracking-tight max-w-3xl">What we do best.</h2>
          </FadeUp>
          <div className="mt-16 divide-y divide-white/10 border-t border-white/10">
            {services.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.08}>
                <div className="group grid grid-cols-12 items-center py-8 md:py-14 md:hover:px-4 transition-all duration-300">
                  <p className="col-span-2 md:col-span-1 font-serif text-white/40 text-lg md:text-2xl">0{i + 1}</p>
                  <h3 className="col-span-10 md:col-span-5 font-serif text-[26px] md:text-5xl tracking-tight leading-tight">{s.title}</h3>
                  <p className="col-span-12 md:col-span-5 mt-3 md:mt-0 text-white/70 text-base md:text-lg">{s.tagline}</p>
                  <ArrowRight className="hidden md:block col-span-1 justify-self-end h-6 w-6 text-white/40 group-hover:text-[#E85D8E] group-hover:translate-x-1 transition-all" />
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

   {/* VIDEO SECTION */}
<section className="py-24 md:py-32 bg-gradient-to-b from-[#1a1a1a] via-[#2b1a24] to-black text-white">
  <div className="container mx-auto px-6 md:px-12 max-w-6xl text-center">

    <FadeUp>
      <SectionLabel className="bg-gradient-to-r from-pink-400 to-[#7A2E5D] bg-clip-text text-transparent tracking-[0.25em] uppercase">
        Our Work
      </SectionLabel>

      <h2 className="font-serif text-4xl md:text-5xl mt-5 tracking-tight text-white drop-shadow-[0_2px_10px_rgba(255,255,255,0.2)]">
        See our projects in action
      </h2>
    </FadeUp>

    <FadeUp delay={0.2}>
      <div className="mt-14 relative group">

        {/* Glow Background */}
        <div className="absolute -inset-1 bg-gradient-to-r from-[#7A2E5D] to-pink-400 blur-xl opacity-30 group-hover:opacity-50 transition duration-500 rounded-[24px]"></div>

        {/* Video Card */}
        <div className="relative overflow-hidden rounded-[24px] shadow-2xl border border-white/10">
          <iframe
            className="w-full h-[260px] md:h-[520px]"
            src="https://www.youtube.com/embed/-U_WUeMja8I"
            title="Sanggavai Construction Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>

      </div>
    </FadeUp>

  </div>
</section>

      {/* GALLERY */}
      <section data-testid="gallery-section" className="py-24 md:py-32 bg-[#F5F2F0]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <SectionLabel>Gallery</SectionLabel>
              <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-5 tracking-tight">Moments of craft.</h2>
            </div>
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 brand-gradient text-white rounded-full px-7 py-3.5 font-medium hover:-translate-y-0.5 hover:shadow-xl transition-all whitespace-nowrap"
            >
              View Full Gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
          {projectsLoading ? (
            <p className="text-center text-[#555] py-16">
              Loading gallery...
            </p>
          ) : homeGalleryImages.length === 0 ? (
            <p className="text-center text-[#555] py-16">
              No gallery images available yet.
            </p>
          ) : (
            <div className="mt-14 masonry columns-1 sm:columns-2 lg:columns-3">
              {homeGalleryImages.map((src, i) => (
                <FadeUp key={src} delay={(i % 4) * 0.05}>
                  <Link
                    to="/gallery"
                    className="overflow-hidden group rounded-[20px] block hover:opacity-95 transition-opacity"
                    style={{ borderRadius: "20px" }}
                  >
                    <img src={src} alt={`Gallery ${i + 1}`} loading="lazy" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" />
                  </Link>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </section>






{/* TESTIMONIALS */}
<section className="py-24 md:py-32 bg-gradient-to-b from-white to-[#f8f5f7]">
  <div className="container mx-auto px-6 md:px-12 max-w-6xl">

    <FadeUp className="text-center max-w-2xl mx-auto">
      <SectionLabel className="text-[#7A2E5D] tracking-[0.25em] uppercase">
        Testimonials
      </SectionLabel>

      <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#1A1A1A] tracking-tight">
        What our clients say
      </h2>
    </FadeUp>

    {!testimonialsLoading &&
      !testimonialsError &&
      testimonials.length > 1 && (
        <FadeUp className="mt-8 flex items-center justify-center gap-3">
          <button
            type="button"
            data-testid="testimonials-prev"
            className="testimonials-prev h-11 w-11 rounded-full border border-[#7A2E5D]/30 text-[#7A2E5D] hover:bg-[#7A2E5D] hover:text-white transition-all inline-flex items-center justify-center"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            type="button"
            data-testid="testimonials-next"
            className="testimonials-next h-11 w-11 rounded-full border border-[#7A2E5D]/30 text-[#7A2E5D] hover:bg-[#7A2E5D] hover:text-white transition-all inline-flex items-center justify-center"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </FadeUp>
      )}

    <div className="mt-12 overflow-hidden px-1">
      {testimonialsLoading && (
        <p className="text-center text-[#555] py-16">
          Loading testimonials...
        </p>
      )}

      {!testimonialsLoading && testimonialsError && (
        <p className="text-center text-[#555] py-16">
          {testimonialsError}
        </p>
      )}

      {!testimonialsLoading &&
        !testimonialsError &&
        testimonials.length === 0 && (
          <p className="text-center text-[#555] py-16">
            Client testimonials will appear here soon.
          </p>
        )}

      {!testimonialsLoading &&
        !testimonialsError &&
        testimonials.length > 0 && (
          <Swiper
            className="testimonials-carousel"
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2, spaceBetween: 24 },
              1024: { slidesPerView: 3, spaceBetween: 32 },
            }}
            navigation={{
              prevEl: ".testimonials-prev",
              nextEl: ".testimonials-next",
            }}
            pagination={{
              clickable: true,
              el: ".testimonials-pagination",
            }}
            autoplay={{
              delay: 5500,
              disableOnInteraction: true,
            }}
            loop={testimonials.length > 3}
            watchOverflow
          >
            {testimonials.map((testimonial, index) => {
              const clientName = testimonial.client_name || "Client";
              const clientInitial =
                clientName.trim().charAt(0).toUpperCase() || "C";
              const photoUrl = makeAbsoluteUrl(
                testimonial.client_photo_url ||
                  testimonial.client_photo ||
                  ""
              );
              const rating = Math.max(
                1,
                Math.min(5, Number(testimonial.rating) || 5)
              );

              return (
                <SwiperSlide
                  key={testimonial.id || `${clientName}-${index}`}
                  className="!h-auto pb-2"
                >
                  <FadeUp className="h-full" delay={index * 0.08}>
                    <div className="group relative bg-white/80 backdrop-blur-xl p-8 rounded-[24px] border border-black/5 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 min-h-[290px] h-full flex flex-col">

                      {/* Quote Icon */}
                      <div className="text-[#7A2E5D]/20 text-5xl font-serif absolute top-4 right-6">
                        “
                      </div>

                      {/* Text */}
                      <p className="text-[#555] text-lg leading-relaxed relative z-10">
                        {testimonial.review}
                      </p>

                      {/* Divider */}
                      <div className="w-10 h-[2px] bg-gradient-to-r from-[#7A2E5D] to-pink-400 mt-6"></div>

                      {/* Profile */}
                      <div className="mt-auto pt-6 flex items-center gap-3">
                        <TestimonialAvatar
                          photoUrl={photoUrl}
                          clientName={clientName}
                          clientInitial={clientInitial}
                        />

                        <div>
                          <p className="font-semibold text-[#1A1A1A]">
                            {clientName}
                          </p>

                          {testimonial.project_name && (
                            <p className="text-sm text-[#777] mt-0.5">
                              {testimonial.project_name}
                            </p>
                          )}

                          {/* Stars */}
                          <div
                            className="flex gap-1 text-sm mt-1"
                            aria-label={`${rating} out of 5 stars`}
                          >
                            {Array.from(
                              { length: 5 },
                              (_, starIndex) => (
                                <span
                                  key={starIndex}
                                  className={
                                    starIndex < rating
                                      ? "text-yellow-400"
                                      : "text-gray-300"
                                  }
                                >
                                  ★
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      </div>

                    </div>
                  </FadeUp>
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}

      {!testimonialsLoading &&
        !testimonialsError &&
        testimonials.length > 1 && (
          <div className="testimonials-pagination !relative !mt-8 flex justify-center gap-2" />
        )}
    </div>
  </div>
</section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" className="py-24 md:py-32 brand-gradient text-white">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl text-center">
          <FadeUp>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight">
              Let's build your <span className="italic font-light">dream project.</span>
            </h2>
            <p className="mt-6 text-white/85 text-lg md:text-xl max-w-xl mx-auto">
              Speak to our team. We'll help you find or build the right space.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="cta-call-btn"
                className="inline-flex items-center gap-2 bg-white text-[#7A2E5D] rounded-full px-8 py-4 font-medium text-base hover:-translate-y-0.5 hover:shadow-xl transition-all"
              >
                Call Now
              </a>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
                className="inline-flex items-center gap-2 border border-white/70 text-white rounded-full px-8 py-4 font-medium text-base hover:bg-white hover:text-[#7A2E5D] transition-all"
              >
                WhatsApp
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

        {/* DYNAMIC MULTI-FORM SECTION */}
  <DynamicMultiForm />
    </div>
  );
}
Home.jsx


